'use es6';
/*
 * Maintains top level application details.
 *
 * This is NOT a bucket to throw any random thing into.
 */

import SeamlessImmutable from 'seamless-immutable';
import { combineReducers } from 'redux';
import { isBoolean, isObject } from 'underscore';
import { DEFAULT } from 'DesignData/enums/AppContexts';
import { NONE } from 'DesignData/enums/DesignAssetTypes';
import { tryParseNumber } from 'DesignData/lib/text';
import PaneSize from 'DesignData/models/PaneSize';
import EditorOptions from 'DesignData/models/app/EditorOptions';
import { APPLICATION_SET_ACTIVE_EDITOR, APPLICATION_TOGGLE_FINDER, APPLICATION_SET_IS_KEYBOARD_SHORTCUTS_LISTING_SHOWING, APPLICATION_HIDE_GLOBAL_CONTENT_MESSAGE, APPLICATION_SHOW_GLOBAL_CONTENT_MESSAGE, FINDER_ASSET_REVEAL_IN_FINDER, APPLICATION_SET_FINDER_WIDTH, APPLICATION_SET_ALWAYS_SHOW_FINDER_ON_START, APPLICATION_UPDATE_PREVIEWER, FETCH_ATTRIBUTES_META, RECEIVE_ATTRIBUTES_META, APPLICATION_DISMISS_RESPONSIVE_IMAGE_WARNING, APPLICATION_DISMISS_SHOW_LOADING_WARNING, APPLICATION_DISMISS_ICON_SET_WARNING, APPLICATION_DISMISS_ADVANCED_REL_WARNING, APPLICATION_DISMISS_ADVANCED_VIDEO_OPTIONS_WARNING, APPLICATION_ADD_PINNED_TAB, APPLICATION_REMOVE_PINNED_TAB } from 'DesignData/actions/ActionTypes';
import { FINDER_WIDTH } from 'DesignData/constants/PaneSizes';
import { PIXELS } from 'DesignData/enums/SizeUnits';
/**
 * activeEditor
 */

function activeEditor(state = SeamlessImmutable({
  assetType: NONE,
  assetId: null,
  editorOptions: new EditorOptions()
}), action) {
  switch (action.type) {
    case APPLICATION_SET_ACTIVE_EDITOR:
      {
        const {
          assetType,
          editorOptions,
          assetId: id
        } = action;
        const assetId = id ? tryParseNumber(id) : null;
        return SeamlessImmutable.merge(state, {
          assetType,
          assetId,
          editorOptions
        }, {
          deep: true
        });
      }

    default:
      return state;
  }
}
/*
 * This is the version of Design Manager
 */


function context(state = DEFAULT) {
  return state;
}

function isFinderShowing(state = true, action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          designManagerUserSettings
        } = action;

        if (designManagerUserSettings && isBoolean(designManagerUserSettings.isFinderShowing)) {
          return designManagerUserSettings.isFinderShowing;
        }

        return state;
      }

    case APPLICATION_TOGGLE_FINDER:
      {
        return action.isShowing;
      }

    case FINDER_ASSET_REVEAL_IN_FINDER:
      {
        const {
          openFinder
        } = action;

        if (!state && openFinder) {
          return true;
        }

        return state;
      }

    default:
      return state;
  }
}

function isKeyboardShortcutsListingShowing(state = false, action) {
  switch (action.type) {
    case APPLICATION_SET_IS_KEYBOARD_SHORTCUTS_LISTING_SHOWING:
      {
        return action.isShowing;
      }

    default:
      return state;
  }
}

function showGlobalContentMessage(state = false, action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          designManagerUserSettings
        } = action;

        if (designManagerUserSettings && isBoolean(designManagerUserSettings.showGlobalContentMessage)) {
          return designManagerUserSettings.showGlobalContentMessage;
        }

        return state;
      }

    case APPLICATION_SHOW_GLOBAL_CONTENT_MESSAGE:
      {
        return action.show;
      }

    case APPLICATION_HIDE_GLOBAL_CONTENT_MESSAGE:
      {
        return false;
      }

    default:
      {
        return state;
      }
  }
}

function hasDismissedResponsiveImageWarning(state = false, action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          designManagerUserSettings
        } = action;

        if (designManagerUserSettings && isBoolean(designManagerUserSettings.hasDismissedResponsiveImageWarning)) {
          return designManagerUserSettings.hasDismissedResponsiveImageWarning;
        }

        return state;
      }

    case APPLICATION_DISMISS_RESPONSIVE_IMAGE_WARNING:
      {
        return true;
      }

    default:
      {
        return state;
      }
  }
}

function hasDismissedAdvancedRelOptionsWarning(state = false, action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          designManagerUserSettings
        } = action;

        if (designManagerUserSettings && isBoolean(designManagerUserSettings.hasDismissedAdvancedRelOptionsWarning)) {
          return designManagerUserSettings.hasDismissedAdvancedRelOptionsWarning;
        }

        return state;
      }

    case APPLICATION_DISMISS_ADVANCED_REL_WARNING:
      {
        return true;
      }

    default:
      {
        return state;
      }
  }
}

function hasDismissedAdvancedVideoOptionsWarning(state = false, action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          designManagerUserSettings
        } = action;

        if (designManagerUserSettings && isBoolean(designManagerUserSettings.hasDismissedAdvancedVideoOptionsWarning)) {
          return designManagerUserSettings.hasDismissedAdvancedVideoOptionsWarning;
        }

        return state;
      }

    case APPLICATION_DISMISS_ADVANCED_VIDEO_OPTIONS_WARNING:
      {
        return true;
      }

    default:
      {
        return state;
      }
  }
}

function hasDismissedShowLoadingControls(state = false, action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          designManagerUserSettings
        } = action;

        if (designManagerUserSettings && isBoolean(designManagerUserSettings.hasDismissedShowLoadingWarning)) {
          return designManagerUserSettings.hasDismissedShowLoadingWarning;
        }

        return state;
      }

    case APPLICATION_DISMISS_SHOW_LOADING_WARNING:
      {
        return true;
      }

    default:
      {
        return state;
      }
  }
}

function hasDismissedIconSetWarning(state = false, action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          designManagerUserSettings
        } = action;

        if (designManagerUserSettings && isBoolean(designManagerUserSettings.hasDismissedIconSetWarning)) {
          return designManagerUserSettings.hasDismissedIconSetWarning;
        }

        return state;
      }

    case APPLICATION_DISMISS_ICON_SET_WARNING:
      {
        return true;
      }

    default:
      {
        return state;
      }
  }
}

function finderWidth(state = new PaneSize({
  value: FINDER_WIDTH,
  units: PIXELS
}), action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          designManagerUserSettings
        } = action;

        if (designManagerUserSettings && isObject(designManagerUserSettings.finderWidth)) {
          return new PaneSize({
            units: designManagerUserSettings.finderWidth.units,
            value: designManagerUserSettings.finderWidth.value
          });
        }

        return state;
      }

    case APPLICATION_SET_FINDER_WIDTH:
      {
        const {
          width
        } = action;
        if (!(width && isObject(width))) return state;
        return width;
      }

    default:
      {
        return state;
      }
  }
}

function alwaysShowFinderOnStartup(state = false, action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          alwaysShowFinderOnStartup: alwaysShow
        } = action.designManagerUserSettings;
        return isBoolean(alwaysShow) ? alwaysShow : state;
      }

    case APPLICATION_SET_ALWAYS_SHOW_FINDER_ON_START:
      {
        return action.alwaysShowFinderOnStartup;
      }

    default:
      {
        return state;
      }
  }
}

function updatePreviewer(state = false, action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          updatePreviewer: autoUpdate
        } = action.designManagerUserSettings;
        return isBoolean(autoUpdate) ? autoUpdate : state;
      }

    case APPLICATION_UPDATE_PREVIEWER:
      {
        return action.updatePreviewer;
      }

    default:
      {
        return state;
      }
  }
}

function pinnedTabs(state = [], action) {
  switch (action.type) {
    case FETCH_ATTRIBUTES_META:
    case RECEIVE_ATTRIBUTES_META:
      {
        const {
          pinnedTabs: newPinnedTabs
        } = action.designManagerUserSettings;
        return newPinnedTabs && newPinnedTabs.length ? newPinnedTabs : state;
      }

    case APPLICATION_ADD_PINNED_TAB:
      {
        const {
          pinnedTabId
        } = action;
        return state.includes(pinnedTabId) ? state : [...state, pinnedTabId];
      }

    case APPLICATION_REMOVE_PINNED_TAB:
      {
        const {
          pinnedTabId
        } = action;
        return state.includes(pinnedTabId) ? state.filter(tabId => tabId !== pinnedTabId) : state;
      }

    default:
      {
        return state;
      }
  }
}
/*
 * Export
 */


export default combineReducers({
  activeEditor,
  context,
  isFinderShowing,
  isKeyboardShortcutsListingShowing,
  showGlobalContentMessage,
  finderWidth,
  alwaysShowFinderOnStartup,
  updatePreviewer,
  hasDismissedResponsiveImageWarning,
  hasDismissedShowLoadingControls,
  hasDismissedIconSetWarning,
  hasDismissedAdvancedRelOptionsWarning,
  hasDismissedAdvancedVideoOptionsWarning,
  pinnedTabs
});